import React, { useState } from "react";
import { Alert, Row, message } from "antd";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";

// graphql
import GET_COURSE_DETAILS from "graphql/queries/GetCourseDetails";

// custom components
import ClassList from "common/class_card/ClassList";

// styling
import * as Layout from "common/layout";
import { SectionButton } from "common/button";
import { SectionTitle } from "common/text";
import BookCourseModal from "./BookCourseModal";

const { PageSection, SectionHeader, HeaderCol } = Layout;

type ParsedCourseDataType = {
  bookableCredits: number;
  hasPastLessons: boolean;
  numberOfFutureLessons: number;
};

type CourseClassesProps = {
  currentCourseId?: number;
};

const CourseClasses = ({ currentCourseId }: CourseClassesProps) => {
  const { t } = useTranslation("book_a_course");

  const {
    data: courseData,
    loading,
    error,
  } = useQuery(GET_COURSE_DETAILS, {
    skip: !currentCourseId,
    variables: {
      id: currentCourseId,
    },
    fetchPolicy: "network-only",
  });

  const [bookingModalVisible, setBookingModalVisible] = useState(false);

  if (error) {
    return (
      <Alert
        type="error"
        message="Error loading classes. Please check your internet or let us know if this keeps happening."
      />
    );
  }

  if (loading) {
    return <Alert type="info" message="Loading..." />;
  }

  const handleClickBookAll = () => {
    const {
      courseDetails: { lessons },
    } = courseData;
    const emptySeatLesson = lessons.filter(
      (lesson: any) => lesson.seatsRemaining === 0
    ).length;

    if (emptySeatLesson === 0) {
      setBookingModalVisible(true);
    } else {
      const totalLessonCount = lessons.length;
      const availableSeatLessonCount = totalLessonCount - emptySeatLesson;

      message.error(
        `Only ${availableSeatLessonCount} out of ${totalLessonCount} classes have available seats. Please manually book each class instead.`
      );
    }
  };

  const courseDataParser = (): ParsedCourseDataType => {
    const parsedCourseData: ParsedCourseDataType = {
      bookableCredits: 0,
      hasPastLessons: false,
      numberOfFutureLessons: 0,
    };
    if (courseData?.courseDetails.lessons.length) {
      // Only calculate credits of future lessons
      const futureLessons = courseData?.courseDetails.lessons.filter(
        (lesson: { startTime: string }) => {
          const currentTime = new Date().getTime();
          const lessonTime = new Date(lesson.startTime).getTime();
          return lessonTime > currentTime;
        }
      );
      if (futureLessons.length < courseData?.courseDetails.lessons.length)
        parsedCourseData.hasPastLessons = true;
      if (futureLessons.length) {
        parsedCourseData.numberOfFutureLessons = futureLessons.length;
        const courseCredits: { credits: number } = futureLessons.reduce(
          (
            previousVal: { credits: number },
            currentVal: { credits: number }
          ) => {
            return { credits: previousVal.credits + currentVal.credits };
          },
          { credits: 0 }
        );
        parsedCourseData.bookableCredits = courseCredits.credits;
      }
    }
    return parsedCourseData;
  };

  const parsedCourseData: ParsedCourseDataType = courseDataParser();

  return (
    <PageSection>
      <SectionHeader>
        <HeaderCol xs={24} sm={24} md={12}>
          <SectionTitle>{t("CLASSES IN THIS COURSE")}</SectionTitle>
        </HeaderCol>
        <HeaderCol xs={24} sm={24} md={4}>
          <SectionButton
            type="primary"
            onClick={handleClickBookAll}
            disabled={!currentCourseId || !parsedCourseData.bookableCredits}
          >
            {t("BOOK ALL")}
          </SectionButton>
        </HeaderCol>
      </SectionHeader>
      <Row gutter={24}>
        <ClassList
          lessons={courseData?.courseDetails.lessons}
          allowsBooking
          showRibbon={{ completed: true, rated: true, past: true }}
        />
      </Row>
      {currentCourseId && parsedCourseData.bookableCredits && (
        <BookCourseModal
          visible={bookingModalVisible}
          onCloseModal={() => {
            setBookingModalVisible(false);
          }}
          cost={parsedCourseData.bookableCredits}
          state={{ courseId: currentCourseId }}
          hasPastLessons={parsedCourseData.hasPastLessons}
          numberOfFutureLessons={parsedCourseData.numberOfFutureLessons}
        />
      )}
    </PageSection>
  );
};

export default CourseClasses;
